import React, { useState, useEffect, Fragment } from 'react';
import axios from 'axios';
import DataTable from 'react-data-table-component';
import { Button, Modal, ModalBody, ModalFooter, Spinner, FormGroup, Label, Input } from 'reactstrap';
import { useSelector } from 'react-redux';
import apiRequest from '../../utils/apiRequest';
import { Breadcrumbs } from '../../AbstractElements';

const Orders = () => {
  const [orders, setOrders] = useState([]);
  const [subAdmins, setSubAdmins] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedSubAdmin, setSelectedSubAdmin] = useState('');
  const [taskDescription, setTaskDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [totalRows, setTotalRows] = useState(0); // To keep track of total rows for pagination
  const token = useSelector((state) => state.auth.userToken);

  const fetchOrders = async (page = 1, rows = 10) => {
    try {
      setLoading(true);
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/orders/admin`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { page, limit: rows }, // Add pagination parameters
      });
      setOrders(response.data.orders);

      setTotalRows(response.data.total); // Assuming your API response includes the total number of rows
      setLoading(false);
    } catch (error) {
      console.error("Error fetching orders:", error);
      setLoading(false);
    }
  };

  const fetchSubAdmins = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/admin/active-subadmins`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSubAdmins(response.data.subAdmins);
    } catch (error) {
      console.error("Error fetching sub-admins:", error);
    }
  };

  useEffect(() => {
    fetchOrders(currentPage, rowsPerPage);
    fetchSubAdmins();
  }, [currentPage, rowsPerPage, token]);

  const handleStatusUpdate = async () => {
    try {
      setLoading(true);
      const data = {
        folderId: selectedOrder.folder._id,
        driveId: selectedOrder.driveFolderId,
      };
      await apiRequest('post', `${process.env.REACT_APP_API_BASE_URL}/orders/orderComplete`, data, token);
      fetchOrders(currentPage, rowsPerPage); // Refresh orders after status update
      setStatusModalOpen(false);
      setLoading(false);
    } catch (error) {
      console.error("Error updating order status:", error);
      setLoading(false);
    }
  };

  const handleTaskAssignment = async () => {
    try {
      setLoading(true);
      const data = {
        subAdminId: selectedSubAdmin,
        orderId: selectedOrder._id,
        taskDescription: taskDescription,
      };
      await apiRequest('post', `${process.env.REACT_APP_API_BASE_URL}/admin/assign`, data, token);
      fetchOrders(currentPage, rowsPerPage); // Refresh orders after assignment
      setAssignModalOpen(false);
      setLoading(false);
    } catch (error) {
      console.error("Error assigning task:", error);
      setLoading(false);
    }
  };

  const handleStatusModal = (order) => {
    setSelectedOrder(order);
    setStatusModalOpen(true);
  };

  const columns = [
    {
      name: 'Order Number',
      selector: row => row.orderNumber,
      sortable: true,
    },
    {
      name: 'Status',
      selector: row => row.status,
      sortable: true,
    },
    {
      name: 'Sub Admin',
      cell: row => (
        row.subAdminName ? (
          <span>{row.subAdminName}</span>
        ) : (
          <Button
            color="primary"
            onClick={() => {
              setSelectedOrder(row);
              setAssignModalOpen(true);
            }}
            disabled={row.status === 'completed'}
          >
            {row.status === 'completed' ? "Done" : "Assign Task"}
          </Button>
        )
      ),
    },
    {
      name: 'Actions',
      cell: row => (
        <Fragment>
          <Button
            color="success"
            onClick={() => handleStatusModal(row)}
            disabled={row.status === 'completed'}
          >
            {row.status === 'completed' ? 'Completed' : 'Mark as Complete'}
          </Button>
        </Fragment>
      ),
    },
  ];

  return (
    <div>
      <Breadcrumbs mainTitle="Orders" parent="Dashboard" title="Orders" />
      <DataTable
        columns={columns}
        data={orders}
        pagination
        paginationServer
        paginationPerPage={rowsPerPage}
        paginationTotalRows={totalRows}
        onChangePage={page => setCurrentPage(page)}
        onChangeRowsPerPage={rows => setRowsPerPage(rows)}
        progressPending={loading}
      />
      {/* Status Update Modal */}
      <Modal isOpen={statusModalOpen} toggle={() => setStatusModalOpen(false)}>
        <ModalBody>
          <p>Are you sure you want to mark this order as complete?</p>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setStatusModalOpen(false)}>Cancel</Button>
          <Button color="primary" onClick={handleStatusUpdate}>
            {loading ? <Spinner size="sm" /> : 'Yes'}
          </Button>
        </ModalFooter>
      </Modal>
      {/* Assign Task Modal */}
      <Modal isOpen={assignModalOpen} toggle={() => setAssignModalOpen(false)}>
        <ModalBody>
          <FormGroup>
            <Label for="subAdmin">Assign Tasks</Label>
            <Input
              type="select"
              id="subAdmin"
              value={selectedSubAdmin}
              onChange={e => setSelectedSubAdmin(e.target.value)}
            >
              <option value="">Select Sub Admin</option>
              {subAdmins.map(subAdmin => (
                <option key={subAdmin._id} value={subAdmin._id}>{subAdmin.userName}</option>
              ))}
            </Input>
          </FormGroup>
          <FormGroup>
            <Label for="taskDescription">Task Description</Label>
            <Input
              type="textarea"
              id="taskDescription"
              value={taskDescription}
              onChange={e => setTaskDescription(e.target.value)}
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setAssignModalOpen(false)}>Cancel</Button>
          <Button color="primary" onClick={handleTaskAssignment} disabled={!selectedSubAdmin}>
            {loading ? <Spinner size="sm" /> : 'Assign Task'}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default Orders;

// import React, { useState, useEffect, Fragment } from 'react';
// import axios from 'axios';
// import DataTable from 'react-data-table-component';
// import { Button, Modal, ModalBody, ModalFooter, Spinner, FormGroup, Label, Input } from 'reactstrap';
// import { useSelector } from 'react-redux';
// import apiRequest from '../../utils/apiRequest';
// import { Breadcrumbs } from '../../AbstractElements';

// const Orders = () => {
//   const [orders, setOrders] = useState([]);
//   const [subAdmins, setSubAdmins] = useState([]);
//   const [currentPage, setCurrentPage] = useState(1);
//   const [rowsPerPage, setRowsPerPage] = useState(10);
//   const [statusModalOpen, setStatusModalOpen] = useState(false);
//   const [assignModalOpen, setAssignModalOpen] = useState(false);
//   const [selectedOrder, setSelectedOrder] = useState(null);
//   const [selectedSubAdmin, setSelectedSubAdmin] = useState('');
//   const [taskDescription, setTaskDescription] = useState('');
//   const [loading, setLoading] = useState(false);
//   const token = useSelector((state) => state.auth.userToken);

//   const fetchOrders = async () => {
//     try {
//       const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/orders`, {
//         headers: { Authorization: `Bearer ${token}` },
//       });
//       setOrders(response.data);
//     } catch (error) {
//       console.error("Error fetching orders:", error);
//     }
//   };

//   const fetchSubAdmins = async () => {
//     try {
//       const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/admin/active-subadmins`, {
//         headers: { Authorization: `Bearer ${token}` },
//       });
//       setSubAdmins(response.data.subAdmins);
//     } catch (error) {
//       console.error("Error fetching sub-admins:", error);
//     }
//   };

//   useEffect(() => {
//     fetchOrders();
//     fetchSubAdmins();
//   }, [token]);

//   const handleStatusUpdate = async () => {
//     try {
//       setLoading(true);
//       const data = {
//         folderId: selectedOrder.folder._id,
//         driveId: selectedOrder.driveFolderId,
//       };
//       await apiRequest('post', `http://localhost:8000/api/orders/orderComplete`, data, token);
//       fetchOrders();
//       setStatusModalOpen(false);
//       setLoading(false);
//     } catch (error) {
//       console.error("Error updating order status:", error);
//       setLoading(false);
//     }
//   };

//   const handleTaskAssignment = async () => {
//     try {
//       setLoading(true);
//       const data = {
//         subAdminId: selectedSubAdmin,
//         orderId: selectedOrder._id,
//         taskDescription: taskDescription,
//       };
//       await apiRequest('post', `${process.env.REACT_APP_API_BASE_URL}/admin/assign`, data, token);
//       fetchOrders();
//       setAssignModalOpen(false);
//       setLoading(false);
//     } catch (error) {
//       console.error("Error assigning task:", error);
//       setLoading(false);
//     }
//   };
//   const handleStatusModal = (order) => {
//     setSelectedOrder(order);
//     setStatusModalOpen(true);
//   };
//   const columns = [
//     {
//       name: 'Order Number',
//       selector: row => row.orderNumber,
//       sortable: true,
//     },
//     {
//       name: 'Status',
//       selector: row => row.status,
//       sortable: true,
//     },
//     {
//       name: 'Sub Admin',
//       cell: row => (
//         row.subAdminName ? (
//           <span>{row.subAdminName}</span>
//         ) : (
//           <Button
//             color="primary"
//             onClick={() => {
//               setSelectedOrder(row);
//               setAssignModalOpen(true);
//             }}
//             disabled={row.status === 'completed'}
//           >
//           {
// row.status === 'completed'?"Done":
//           "Assign Task"} 
//           </Button>
//         )
//       ),
//     },
//     {
//       name: 'Actions',
//       cell: row => (
//         <Fragment>
//             <Button
//             color="success"
//             onClick={() => handleStatusModal(row)}
//             disabled={row.status === 'completed'}
//           >
//             {row.status === 'completed' ? 'Completed' : 'Mark as Complete'}
//           </Button>
//         </Fragment>
//       ),
//     },
//   ];

//   return (
//     <div>
//       <Breadcrumbs mainTitle="Orders" parent="Dashboard" title="Orders"/>
//       <DataTable
//         columns={columns}
//         data={orders}
//         pagination
//         paginationServer
//         paginationPerPage={rowsPerPage}
//         paginationTotalRows={orders.length}
//         onChangePage={page => setCurrentPage(page)}
//         onChangeRowsPerPage={rows => setRowsPerPage(rows)}
//         progressPending={loading}
//       />
//       {/* Status Update Modal */}
//       <Modal isOpen={statusModalOpen} toggle={() => setStatusModalOpen(false)}>
//         <ModalBody>
//           <p>Are you sure you want to mark this order as complete?</p>
//         </ModalBody>
//         <ModalFooter>
//           <Button color="secondary" onClick={() => setStatusModalOpen(false)}>Cancel</Button>
//           <Button color="primary" onClick={handleStatusUpdate}>
//             {loading ? <Spinner size="sm" /> : 'Yes'}
//           </Button>
//         </ModalFooter>
//       </Modal>
//       {/* Assign Task Modal */}
//       <Modal isOpen={assignModalOpen} toggle={() => setAssignModalOpen(false)}>
//         <ModalBody>
//           <FormGroup>
//             <Label for="subAdmin">Assign Tasks</Label>
//             <Input
//               type="select"
//               id="subAdmin"
//               value={selectedSubAdmin}
//               onChange={e => setSelectedSubAdmin(e.target.value)}
//             >
//               <option value="">Select Sub Admin</option>
//               {subAdmins.map(subAdmin => (
//                 <option key={subAdmin._id} value={subAdmin._id}>{subAdmin.userName}</option>
//               ))}
//             </Input>
//           </FormGroup>
//           <FormGroup>
//             <Label for="taskDescription">Task Description</Label>
//             <Input
//               type="textarea"
//               id="taskDescription"
//               value={taskDescription}
//               onChange={e => setTaskDescription(e.target.value)}
//             />
//           </FormGroup>
//         </ModalBody>
//         <ModalFooter>
//           <Button color="secondary" onClick={() => setAssignModalOpen(false)}>Cancel</Button>
//           <Button color="primary" onClick={handleTaskAssignment}              disabled={!selectedSubAdmin }
//           >
//             {loading ? <Spinner size="sm" /> : 'Assign Task'}
//           </Button>
//         </ModalFooter>
//       </Modal>
//     </div>
//   );
// };

// export default Orders;

// import React, { useState, useEffect, Fragment } from 'react';
// import axios from 'axios';
// import DataTable from 'react-data-table-component';
// import { Button, Modal, ModalBody, ModalFooter, Spinner, FormGroup, Label, Input } from 'reactstrap';
// import { useSelector } from 'react-redux';
// import apiRequest from '../../utils/apiRequest';
// import { Breadcrumbs } from '../../AbstractElements';

// const Orders = () => {
//   const [orders, setOrders] = useState([]);
//   const [subAdmins, setSubAdmins] = useState([]);
//   const [currentPage, setCurrentPage] = useState(1);
//   const [rowsPerPage, setRowsPerPage] = useState(10);
//   const [statusModalOpen, setStatusModalOpen] = useState(false);
//   const [assignModalOpen, setAssignModalOpen] = useState(false);
//   const [selectedOrder, setSelectedOrder] = useState(null);
//   const [selectedSubAdmin, setSelectedSubAdmin] = useState('');
//   const [loading, setLoading] = useState(false);
//   const token = useSelector((state) => state.auth.userToken);
//   const [taskDescription, setTaskDescription] = useState('');

//   const fetchOrders = async () => {
//     try {
//       const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/orders`, {
//         headers: { Authorization: `Bearer ${token}` },
//       });
      
//       setOrders(response.data);
//     } catch (error) {
//       console.error("Error fetching orders:", error);
//     }
//   };

//   const fetchSubAdmins = async () => {
//     try {
//       const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/admin/active-subadmins`, {
//         headers: { Authorization: `Bearer ${token}` },
//       });
//       setSubAdmins(response.data.subAdmins);
//     } catch (error) {
//       console.error("Error fetching sub-admins:", error);
//     }
//   };

//   useEffect(() => {
//     fetchOrders();
//     fetchSubAdmins();
//   }, []);

//   const handleStatusUpdate = async () => {
//     try {
//       setLoading(true);
//       const data = {
//         folderId: selectedOrder.folder._id,
//         driveId: selectedOrder.driveFolderId,
//       };
//       const response = await apiRequest('post', `http://localhost:8000/api/orders/orderComplete`, data, token);
//       fetchOrders();
//       setStatusModalOpen(false);
//     } catch (error) {
//       console.error("Error updating status:", error);
//     } finally {
//       setLoading(false);
//     }
//   };

//   const handleAssignTask = async () => {
//     try {
//       setLoading(true);
//       const data = {
//         orderId: selectedOrder._id,
//         subAdminId: selectedSubAdmin,
//         taskDescription:taskDescription
//       };
//       const response = await apiRequest('post', `http://localhost:8000/api/admin/assign`, data, token);
//       fetchOrders();
//       setAssignModalOpen(false);
//     } catch (error) {
//       console.error("Error assigning task:", error);
//     } finally {
//       setLoading(false);
//     }
//   };

//   const handleStatusModal = (order) => {
//     setSelectedOrder(order);
//     setStatusModalOpen(true);
//   };

//   const handleAssignModal = (order) => {
//     setSelectedOrder(order);
//     setAssignModalOpen(true);
//   };

//   const columns = [
//     {
//       name: 'Order Number',
//       selector: (row) => row.orderNumber,
//       sortable: true,
//     },
//     {
//       name: 'Order Date',
//       selector: (row) => row.orderDate,
//       sortable: true,
//     },
//     {
//       name: 'Total Amount',
//       selector: (row) => `$${row.totalAmount}`,
//       sortable: true,
//     },
//     {
//       name: 'Status',
//       selector: (row) => row.status,
//       sortable: true,
//     },
//     {
//       name: 'Payment Confirmed',
//       selector: (row) => (row.paymentConfirmed ? 'Yes' : 'No'),
//       sortable: true,
//     },
//     {
//       name: 'Actions',
//       cell: (row) => (
//         <>
//           <Button
//             color="success"
//             onClick={() => handleStatusModal(row)}
//             disabled={row.status === 'completed'}
//           >
//             {row.status === 'completed' ? 'Completed' : 'Mark as Complete'}
//           </Button>
//           <Button
//             color="primary"
//             onClick={() => handleAssignModal(row)}
//             className="ml-2"
//           >
//             Assign Task
//           </Button>
//         </>
//       ),
//       button: true,
//       minWidth: "400px",
//     },
//   ];

//   const handlePageChange = (page) => {
//     setCurrentPage(page);
//   };

//   const handlePerRowsChange = async (newPerPage, page) => {
//     setRowsPerPage(newPerPage);
//   };

//   return (
//     <Fragment>
//       <Breadcrumbs mainTitle="Orders" parent="Dashboard" />
//     <div className="container-fluid">
//       <div className="table-responsive mt-3">
//         <DataTable
//           columns={columns}
//           data={orders.slice(
//             (currentPage - 1) * rowsPerPage,
//             currentPage * rowsPerPage
//           )}
//           pagination
//           paginationServer
//           paginationTotalRows={orders.length}
//           onChangePage={handlePageChange}
//           onChangeRowsPerPage={handlePerRowsChange}
//           paginationRowsPerPageOptions={[10, 20, 30]}
//         />
//       </div>

//       {/* Status Update Modal */}
//       <Modal isOpen={statusModalOpen} toggle={() => setStatusModalOpen(!statusModalOpen)}>
//         <ModalBody>
//           Are you sure you want to mark this order as complete?
//         </ModalBody>
//         <ModalFooter>
//           <Button color="secondary" onClick={() => setStatusModalOpen(!statusModalOpen)}>
//             Cancel
//           </Button>
//           <Button color="primary" onClick={handleStatusUpdate}>
//             {loading ? <Spinner size="sm" /> : 'Confirm'}
//           </Button>
//         </ModalFooter>
//       </Modal>

//       {/* Task Assignment Modal */}
//       <Modal isOpen={assignModalOpen} toggle={() => setAssignModalOpen(!assignModalOpen)}>
//         <ModalBody>
//           <FormGroup>
//             <Label for="subAdminSelect">Assign Sub-Admin</Label>
//             <Input
//               type="select"
//               name="select"
//               id="subAdminSelect"
//               value={selectedSubAdmin}
//               onChange={(e) => setSelectedSubAdmin(e.target.value)}
//             >
//               <option value="">Select Sub-Admin</option>
//               {subAdmins.map((subAdmin) => (
//                 <option key={subAdmin._id} value={subAdmin._id}>
//                   {subAdmin.userName} ({subAdmin.email})
//                 </option>
//               ))}
//             </Input>
//           </FormGroup>
//           <FormGroup>
//             <Label for="taskDescription">Task Description</Label>
//             <Input
//               type="textarea"
//               id="taskDescription"
//               value={taskDescription}
//               onChange={(e) => setTaskDescription(e.target.value)}
//             />
//           </FormGroup>
//         </ModalBody>
//         <ModalFooter>
//           <Button color="secondary" onClick={() => setAssignModalOpen(!assignModalOpen)}>
//             Cancel
//           </Button>
//           <Button color="primary" onClick={handleAssignTask}>
//             {loading ? <Spinner size="sm" /> : 'Assign'}
//           </Button>
//         </ModalFooter>
//       </Modal>
//     </div>
//     </Fragment>

//   );
// };

// export default Orders;
